import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Home, Clock, CreditCard, ChevronRight, X, DollarSign, Calendar, Zap, Droplets, Wrench, Users, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface Contribution {
  id: number;
  type: 'electricity' | 'water' | 'repairs';
  amount: number;
  date: string;
  status: 'pending' | 'completed';
}
interface UtilityBill {
  type: 'electricity' | 'water' | 'repairs';
  totalAmount: number;
  contributors: number;
  yourShare: number;
  paid: number;
  remaining: number;
  dueDate: string;
  icon: any;
  color: string;
}
export function StudentDashboard() {
  const navigate = useNavigate();
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showUtilityModal, setShowUtilityModal] = useState(false);
  const [selectedUtility, setSelectedUtility] = useState<UtilityBill | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [contributionAmount, setContributionAmount] = useState('');
  const [maintenanceIssue, setMaintenanceIssue] = useState('');
  const [contributions, setContributions] = useState<Contribution[]>([{
    id: 1,
    type: 'electricity',
    amount: 5000,
    date: '2024-01-15',
    status: 'completed'
  }, {
    id: 2,
    type: 'water',
    amount: 2000,
    date: '2024-01-15',
    status: 'completed'
  }, {
    id: 3,
    type: 'electricity',
    amount: 5000,
    date: '2024-02-15',
    status: 'pending'
  }]);
  const utilityBills: UtilityBill[] = [{
    type: 'electricity',
    totalAmount: 45000,
    contributors: 9,
    yourShare: 5000,
    paid: 5000,
    remaining: 0,
    dueDate: 'Feb 28, 2024',
    icon: Zap,
    color: 'from-yellow-500 to-orange-600'
  }, {
    type: 'water',
    totalAmount: 18000,
    contributors: 9,
    yourShare: 2000,
    paid: 2000,
    remaining: 0,
    dueDate: 'Feb 28, 2024',
    icon: Droplets,
    color: 'from-blue-500 to-cyan-600'
  }, {
    type: 'repairs',
    totalAmount: 27000,
    contributors: 9,
    yourShare: 3000,
    paid: 0,
    remaining: 3000,
    dueDate: 'Feb 25, 2024',
    icon: Wrench,
    color: 'from-purple-500 to-pink-600'
  }];
  const handleBrowseHostels = () => {
    navigate('/find-hostel');
  };
  const handlePaymentHistory = () => {
    alert('Payment History: View all your past transactions and receipts');
  };
  const handleMaintenance = () => {
    setShowMaintenanceModal(true);
  };
  const handleSupport = () => {
    alert('Support: Our team is here to help! Contact us at support@campusaparte.com or call +234 800 CAMPUS');
  };
  const submitPayment = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Payment of ₦${paymentAmount} initiated! You will receive a confirmation shortly.`);
    setShowPaymentModal(false);
    setPaymentAmount('');
  };
  const submitMaintenance = (e: React.FormEvent) => {
    e.preventDefault();
    alert(`Maintenance request submitted: "${maintenanceIssue}". Our team will respond within 24 hours.`);
    setShowMaintenanceModal(false);
    setMaintenanceIssue('');
  };
  const handleUtilityClick = (utility: UtilityBill) => {
    setSelectedUtility(utility);
    setContributionAmount(utility.remaining.toString());
    setShowUtilityModal(true);
  };
  const submitContribution = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUtility) return;
    const newContribution: Contribution = {
      id: contributions.length + 1,
      type: selectedUtility.type,
      amount: parseInt(contributionAmount),
      date: new Date().toISOString().split('T')[0],
      status: 'pending'
    };
    setContributions([...contributions, newContribution]);
    alert(`Contribution of ₦${contributionAmount} for ${selectedUtility.type} submitted successfully! Your payment is being processed.`);
    setShowUtilityModal(false);
    setContributionAmount('');
    setSelectedUtility(null);
  };
  const getUtilityIcon = (type: string) => {
    switch (type) {
      case 'electricity':
        return Zap;
      case 'water':
        return Droplets;
      case 'repairs':
        return Wrench;
      default:
        return CreditCard;
    }
  };
  const totalPaidThisMonth = contributions.filter(c => c.date.startsWith('2024-02') && c.status === 'completed').reduce((sum, c) => sum + c.amount, 0);
  const totalPendingThisMonth = contributions.filter(c => c.date.startsWith('2024-02') && c.status === 'pending').reduce((sum, c) => sum + c.amount, 0);
  return <DashboardLayout userType="student" data-id="element-1094">
      {/* Payment Modal */}
      <AnimatePresence data-id="element-1095">
        {showPaymentModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowPaymentModal(false)} data-id="element-1096">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-id="element-1097">
              <div className="flex justify-between items-center mb-6" data-id="element-1098">
                <h3 className="text-xl font-bold text-brand-dark" data-id="element-1099">
                  Make Payment
                </h3>
                <button onClick={() => setShowPaymentModal(false)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-1100">
                  <X className="w-5 h-5" data-id="element-1101" />
                </button>
              </div>

              <form onSubmit={submitPayment} className="space-y-4" data-id="element-1102">
                <div className="bg-slate-50 rounded-lg p-4 mb-4" data-id="element-1103">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-1104">
                    Current Balance Due
                  </div>
                  <div className="text-2xl font-bold text-brand-dark" data-id="element-1105">
                    ₦0.00
                  </div>
                </div>

                <Input label="Payment Amount (₦)" type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} placeholder="Enter amount" required data-id="element-1106" />

                <div data-id="element-1107">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1108">
                    Payment Method
                  </label>
                  <select className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary" data-id="element-1109">
                    <option data-id="element-1110">Card Payment</option>
                    <option data-id="element-1111">Bank Transfer</option>
                    <option data-id="element-1112">USSD</option>
                  </select>
                </div>

                <Button type="submit" className="w-full" data-id="element-1113">
                  <DollarSign className="w-4 h-4 mr-2" data-id="element-1114" />
                  Proceed to Payment
                </Button>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Maintenance Modal */}
      <AnimatePresence data-id="element-1115">
        {showMaintenanceModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowMaintenanceModal(false)} data-id="element-1116">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-id="element-1117">
              <div className="flex justify-between items-center mb-6" data-id="element-1118">
                <h3 className="text-xl font-bold text-brand-dark" data-id="element-1119">
                  Maintenance Request
                </h3>
                <button onClick={() => setShowMaintenanceModal(false)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-1120">
                  <X className="w-5 h-5" data-id="element-1121" />
                </button>
              </div>

              <form onSubmit={submitMaintenance} className="space-y-4" data-id="element-1122">
                <div data-id="element-1123">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1124">
                    Issue Type
                  </label>
                  <select className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary" data-id="element-1125">
                    <option data-id="element-1126">Plumbing</option>
                    <option data-id="element-1127">Electrical</option>
                    <option data-id="element-1128">AC/Heating</option>
                    <option data-id="element-1129">Security</option>
                    <option data-id="element-1130">Other</option>
                  </select>
                </div>

                <div data-id="element-1131">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1132">
                    Description
                  </label>
                  <textarea value={maintenanceIssue} onChange={e => setMaintenanceIssue(e.target.value)} placeholder="Describe the issue in detail..." className="w-full h-32 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary resize-none" required data-id="element-1133" />
                </div>

                <Button type="submit" className="w-full" data-id="element-1134">
                  Submit Request
                </Button>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Utility Contribution Modal */}
      <AnimatePresence data-id="element-1135">
        {showUtilityModal && selectedUtility && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowUtilityModal(false)} data-id="element-1136">
            <motion.div initial={{
          scale: 0.9,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.9,
          y: 20
        }} className="bg-white rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-id="element-1137">
              <div className="flex justify-between items-center mb-6" data-id="element-1138">
                <h3 className="text-xl font-bold text-brand-dark capitalize" data-id="element-1139">
                  {selectedUtility.type} Contribution
                </h3>
                <button onClick={() => setShowUtilityModal(false)} className="p-2 hover:bg-slate-100 rounded-full" data-id="element-1140">
                  <X className="w-5 h-5" data-id="element-1141" />
                </button>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 mb-6" data-id="element-1142">
                <div className="flex items-center justify-between mb-4" data-id="element-1143">
                  <div data-id="element-1144">
                    <div className="text-sm text-slate-500 mb-1" data-id="element-1145">
                      Total Bill
                    </div>
                    <div className="text-2xl font-bold text-brand-dark" data-id="element-1146">
                      ₦{selectedUtility.totalAmount.toLocaleString()}
                    </div>
                  </div>
                  <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${selectedUtility.color} flex items-center justify-center`} data-id="element-1147">
                    <selectedUtility.icon className="w-8 h-8 text-white" data-id="element-1148" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-1149">
                  <div data-id="element-1150">
                    <div className="text-slate-500" data-id="element-1151">Your Share</div>
                    <div className="font-bold text-brand-dark" data-id="element-1152">
                      ₦{selectedUtility.yourShare.toLocaleString()}
                    </div>
                  </div>
                  <div data-id="element-1153">
                    <div className="text-slate-500" data-id="element-1154">Contributors</div>
                    <div className="font-bold text-brand-dark" data-id="element-1155">
                      {selectedUtility.contributors} people
                    </div>
                  </div>
                  <div data-id="element-1156">
                    <div className="text-slate-500" data-id="element-1157">Paid</div>
                    <div className="font-bold text-emerald-600" data-id="element-1158">
                      ₦{selectedUtility.paid.toLocaleString()}
                    </div>
                  </div>
                  <div data-id="element-1159">
                    <div className="text-slate-500" data-id="element-1160">Remaining</div>
                    <div className="font-bold text-red-600" data-id="element-1161">
                      ₦{selectedUtility.remaining.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between" data-id="element-1162">
                  <span className="text-sm text-slate-600" data-id="element-1163">Due Date</span>
                  <span className="text-sm font-bold text-brand-dark" data-id="element-1164">
                    {selectedUtility.dueDate}
                  </span>
                </div>
              </div>

              <form onSubmit={submitContribution} className="space-y-4" data-id="element-1165">
                <Input label="Contribution Amount (₦)" type="number" value={contributionAmount} onChange={e => setContributionAmount(e.target.value)} placeholder="Enter amount" required data-id="element-1166" />

                <div className="flex gap-2" data-id="element-1167">
                  <Button type="button" variant="outline" size="sm" onClick={() => setContributionAmount((selectedUtility.yourShare / 2).toString())} data-id="element-1168">
                    Half (₦{(selectedUtility.yourShare / 2).toLocaleString()})
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setContributionAmount(selectedUtility.yourShare.toString())} data-id="element-1169">
                    Full (₦{selectedUtility.yourShare.toLocaleString()})
                  </Button>
                </div>

                <div data-id="element-1170">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-1171">
                    Payment Method
                  </label>
                  <select className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary" data-id="element-1172">
                    <option data-id="element-1173">Card Payment</option>
                    <option data-id="element-1174">Bank Transfer</option>
                    <option data-id="element-1175">USSD</option>
                  </select>
                </div>

                <Button type="submit" className="w-full" data-id="element-1176">
                  <DollarSign className="w-4 h-4 mr-2" data-id="element-1177" />
                  Pay ₦{parseInt(contributionAmount || '0').toLocaleString()}
                </Button>
              </form>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-1178">
        <Card className="bg-gradient-to-br from-brand-primary to-purple-600 text-white border-none" data-id="element-1179">
          <CardContent className="p-6" data-id="element-1180">
            <div className="flex justify-between items-start mb-4" data-id="element-1181">
              <div className="bg-white/20 p-2 rounded-lg" data-id="element-1182">
                <Home className="w-6 h-6 text-white" data-id="element-1183" />
              </div>
              <Badge className="bg-white/20 text-white border-none" data-id="element-1184">
                Active
              </Badge>
            </div>
            <div className="text-3xl font-bold mb-1" data-id="element-1185">Emerald Heights</div>
            <div className="text-purple-100 text-sm" data-id="element-1186">
              Room 302 • Expires in 45 days
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1187">
          <CardContent className="p-6" data-id="element-1188">
            <div className="flex justify-between items-start mb-4" data-id="element-1189">
              <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600" data-id="element-1190">
                <CreditCard className="w-6 h-6" data-id="element-1191" />
              </div>
            </div>
            <div className="text-3xl font-bold text-brand-dark mb-1" data-id="element-1192">
              ₦{totalPaidThisMonth.toLocaleString()}
            </div>
            <div className="text-slate-500 text-sm" data-id="element-1193">
              Utilities paid this month
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1194">
          <CardContent className="p-6" data-id="element-1195">
            <div className="flex justify-between items-start mb-4" data-id="element-1196">
              <div className="bg-emerald-100 p-2 rounded-lg text-emerald-600" data-id="element-1197">
                <Users className="w-6 h-6" data-id="element-1198" />
              </div>
            </div>
            <div className="text-3xl font-bold text-brand-dark mb-1" data-id="element-1199">9</div>
            <div className="text-slate-500 text-sm" data-id="element-1200">Roommates contributing</div>
          </CardContent>
        </Card>
      </div>

      {/* Utility Bills Section */}
      <Card className="mb-8" data-id="element-1201">
        <CardHeader data-id="element-1202">
          <CardTitle data-id="element-1203">Monthly Utility Contributions</CardTitle>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1204">
            Split bills fairly with your roommates
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1205">
          {utilityBills.map((utility, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: idx * 0.1
        }} className="relative overflow-hidden rounded-xl border border-slate-200 hover:shadow-lg transition-all cursor-pointer" onClick={() => handleUtilityClick(utility)} data-id="element-1206">
              <div className={`absolute inset-0 bg-gradient-to-br ${utility.color} opacity-5`} data-id="element-1207"></div>

              <div className="relative p-6" data-id="element-1208">
                <div className="flex items-center justify-between mb-4" data-id="element-1209">
                  <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${utility.color} flex items-center justify-center`} data-id="element-1210">
                    <utility.icon className="w-6 h-6 text-white" data-id="element-1211" />
                  </div>
                  {utility.remaining === 0 ? <CheckCircle className="w-6 h-6 text-emerald-600" data-id="element-1212" /> : <AlertCircle className="w-6 h-6 text-orange-500" data-id="element-1213" />}
                </div>

                <h3 className="text-lg font-bold text-brand-dark capitalize mb-1" data-id="element-1214">
                  {utility.type}
                </h3>
                <p className="text-sm text-slate-500 mb-4" data-id="element-1215">
                  Due: {utility.dueDate}
                </p>

                <div className="space-y-2 mb-4" data-id="element-1216">
                  <div className="flex justify-between text-sm" data-id="element-1217">
                    <span className="text-slate-600" data-id="element-1218">Your Share:</span>
                    <span className="font-bold text-brand-dark" data-id="element-1219">
                      ₦{utility.yourShare.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1220">
                    <span className="text-slate-600" data-id="element-1221">Paid:</span>
                    <span className="font-bold text-emerald-600" data-id="element-1222">
                      ₦{utility.paid.toLocaleString()}
                    </span>
                  </div>
                  {utility.remaining > 0 && <div className="flex justify-between text-sm" data-id="element-1223">
                      <span className="text-slate-600" data-id="element-1224">Remaining:</span>
                      <span className="font-bold text-red-600" data-id="element-1225">
                        ₦{utility.remaining.toLocaleString()}
                      </span>
                    </div>}
                </div>

                {/* Progress Bar */}
                <div className="mb-4" data-id="element-1226">
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-1227">
                    <div className={`h-full bg-gradient-to-r ${utility.color} transition-all duration-500`} style={{
                  width: `${utility.paid / utility.yourShare * 100}%`
                }} data-id="element-1228"></div>
                  </div>
                </div>

                <Button size="sm" className="w-full" variant={utility.remaining === 0 ? 'outline' : 'default'} disabled={utility.remaining === 0} data-id="element-1229">
                  {utility.remaining === 0 ? 'Paid in Full' : 'Contribute Now'}
                </Button>
              </div>
            </motion.div>)}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1230">
        {/* Contribution History */}
        <Card data-id="element-1231">
          <CardHeader className="flex flex-row items-center justify-between" data-id="element-1232">
            <CardTitle data-id="element-1233">Contribution History</CardTitle>
            <Button variant="ghost" size="sm" data-id="element-1234">
              View All
            </Button>
          </CardHeader>
          <CardContent className="p-0" data-id="element-1235">
            {contributions.slice().reverse().map((contribution, idx) => {
            const Icon = getUtilityIcon(contribution.type);
            return <div key={contribution.id} className="p-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors flex items-center justify-between" data-id="element-1236">
                    <div className="flex items-center gap-3" data-id="element-1237">
                      <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center" data-id="element-1238">
                        <Icon className="w-5 h-5 text-slate-600" data-id="element-1239" />
                      </div>
                      <div data-id="element-1240">
                        <div className="font-medium text-brand-dark capitalize" data-id="element-1241">
                          {contribution.type}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-1242">
                          {contribution.date}
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1243">
                      <div className="font-bold text-brand-dark" data-id="element-1244">
                        -₦{contribution.amount.toLocaleString()}
                      </div>
                      <Badge variant={contribution.status === 'completed' ? 'success' : 'warning'} className="text-xs" data-id="element-1245">
                        {contribution.status}
                      </Badge>
                    </div>
                  </div>;
          })}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card data-id="element-1246">
          <CardHeader data-id="element-1247">
            <CardTitle data-id="element-1248">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4" data-id="element-1249">
            <Button variant="outline" className="h-auto py-6 flex flex-col gap-2 hover:border-brand-primary hover:text-brand-primary hover:bg-brand-primary/5" onClick={handleBrowseHostels} data-id="element-1250">
              <Home className="w-6 h-6" data-id="element-1251" />
              <span data-id="element-1252">Browse Hostels</span>
            </Button>
            <Button variant="outline" className="h-auto py-6 flex flex-col gap-2 hover:border-indigo-500 hover:text-indigo-600 hover:bg-indigo-50" onClick={handlePaymentHistory} data-id="element-1253">
              <CreditCard className="w-6 h-6" data-id="element-1254" />
              <span data-id="element-1255">Payment History</span>
            </Button>
            <Button variant="outline" className="h-auto py-6 flex flex-col gap-2 hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-50" onClick={handleMaintenance} data-id="element-1256">
              <Clock className="w-6 h-6" data-id="element-1257" />
              <span data-id="element-1258">Maintenance</span>
            </Button>
            <Button variant="outline" className="h-auto py-6 flex flex-col gap-2 hover:border-slate-500 hover:text-brand-dark hover:bg-slate-50" onClick={handleSupport} data-id="element-1259">
              <ChevronRight className="w-6 h-6" data-id="element-1260" />
              <span data-id="element-1261">Support</span>
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>;
}